import { useTranslation } from 'react-i18next';
import { NodeMenuId } from './NodeMenuId';
import type { NodeStyle } from '../../domain';
import { NodeFontSize } from '../../domain';
import type { FontSize } from '@model-framework/text';
import { FontSizeMenu } from '@model-framework/text';
import { ColorMenu } from '@model-framework/color/ColorMenu';
import { TrashMenuButton } from '@model-framework/menu';
import { HyperLinkMenuButton } from '@model-framework/hyperlink/HyperLinkMenuButton';
import { StickyNodeGroupingMenuButton } from '@model-framework/menu/StickyNodeGroupingMenuButton';
import { ElementDescriptionMenuButton } from '@model-framework/menu/ElementDescriptionMenuButton';
import { ThemeColor } from '@view-model/models/common/color';
import type { TextAlign } from '@view-model/models/common/text-align';
import { TextAlignMenu } from '@view-model/models/common/text-align';
import type { NodeReaction, ReactionType } from '@view-model/models/sticky/NodeReaction';
import { NodeReactionMenu } from '@view-model/models/sticky/NodeReaction';
import type { UserId } from 'balus-common';
import { FontStrikethroughMenuButton } from '@model-framework/menu/FontStrikethroughMenuButton';

type Props = {
  menuId: NodeMenuId;
  menuSize: number;
  currentStyle: NodeStyle;
  isOpen: boolean;
  url: string | null;
  reaction: NodeReaction | null | undefined;
  onMenuOpen(menu: NodeMenuId): void;
  onMenuClose(): void;
  onFontSizeSelected(fontSize: FontSize): void;
  onToggleFontStrikethrough(): void;
  onThemeColorSelected(themeColor: ThemeColor): void;
  onTextAlignSelected(textAlign: TextAlign): void;
  onDeleteNode(): void;
  onToggleNodeDescription(): void;
  onGroupSelectedNode(): void;
  onSaveHyperLink(url: string | null): void;
  onReactionSelected(reactionName: ReactionType, userId: UserId, userName: string): void;
};

export const NodeMenuContent: React.FC<Props> = ({
  menuId,
  menuSize,
  currentStyle,
  isOpen,
  url,
  reaction,
  onMenuOpen,
  onMenuClose,
  onThemeColorSelected,
  onFontSizeSelected,
  onToggleFontStrikethrough,
  onTextAlignSelected,
  onDeleteNode,
  onToggleNodeDescription,
  onGroupSelectedNode,
  onSaveHyperLink,
  onReactionSelected,
}: Props) => {
  const { t } = useTranslation();
  return (
    <>
      {menuId === NodeMenuId.FontSize && (
        <FontSizeMenu
          currentFontSize={currentStyle.fontSize}
          menuSize={menuSize}
          items={NodeFontSize.values()}
          isOpen={isOpen}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onSelected={onFontSizeSelected}
          text={t('canvas.menu.textSize')}
        />
      )}
      {menuId === NodeMenuId.ThemeColor && (
        <ColorMenu
          currentColorName={currentStyle.themeColor}
          menuSize={menuSize}
          items={ThemeColor.menuItems()}
          numColumns={4}
          isOpen={isOpen}
          text={t('canvas.menu.color')}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onSelected={onThemeColorSelected}
        />
      )}
      {menuId === NodeMenuId.TextAlign && (
        <TextAlignMenu
          currentTextAlign={currentStyle.textAlign}
          menuSize={menuSize}
          isOpen={isOpen}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onSelected={onTextAlignSelected}
          text={t('canvas.menu.textAlign')}
        />
      )}
      {menuId === NodeMenuId.FontStrikethrough && (
        <FontStrikethroughMenuButton
          menuSize={menuSize}
          onClick={onToggleFontStrikethrough}
          text={t('canvas.menu.strikethrough')}
        />
      )}
      {menuId === NodeMenuId.HyperLink && (
        <HyperLinkMenuButton
          menuSize={menuSize}
          isOpen={isOpen}
          initialURL={url}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onClose={() => onMenuClose()}
          onSave={onSaveHyperLink}
          text={t('canvas.menu.hyperlink')}
        />
      )}
      {menuId === NodeMenuId.Reaction && (
        <NodeReactionMenu
          menuSize={menuSize}
          isOpen={isOpen}
          reaction={reaction}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onReactionSelected={onReactionSelected}
          text={t('canvas.menu.node.reaction')}
        />
      )}
      {menuId === NodeMenuId.DeleteNode && (
        <TrashMenuButton menuSize={menuSize} onClick={onDeleteNode} text={t('common.action.delete')} />
      )}
      {menuId === NodeMenuId.NodeDescription && (
        <ElementDescriptionMenuButton
          menuSize={menuSize}
          onClick={onToggleNodeDescription}
          text={t('canvas.menu.note')}
        />
      )}
      {menuId === NodeMenuId.Grouping && (
        <StickyNodeGroupingMenuButton
          menuSize={menuSize}
          onClick={() => {
            // 他のメニューが開いていた場合閉じる
            onMenuClose();
            onGroupSelectedNode();
          }}
          text={t('canvas.menu.group')}
        />
      )}
    </>
  );
};
